import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Music4, ImageIcon, FileText, Loader2, Download, Sparkles, Eye, Link2, Upload, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

export const Shell = ({ icon: Icon, title, blurb, children, testid }) => (
  <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid={testid}>
    <Link to="/chat" data-testid="back-to-chat" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
      <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Back to AI Chat
    </Link>
    <div className="mt-6 flex items-start gap-4">
      <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/15 text-brand">
        <Icon className="h-6 w-6" />
      </span>
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">{title}</h1>
        <p className="mt-2 max-w-2xl text-sm text-ink-soft md:text-base">{blurb}</p>
      </div>
    </div>
    <div className="mt-9">{children}</div>
  </div>
);

export function MusicGeneration() {
  const [meta, setMeta] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [genre, setGenre] = useState("None");
  const [duration, setDuration] = useState(8);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    api.get("/tools/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Describe the music you want");
    setBusy(true);
    setNotice("");
    try {
      await api.post("/tools/music", { prompt, genre, duration });
    } catch (e) {
      setNotice(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      icon={Music4}
      title="Music Generation"
      blurb="Describe a track, pick a genre and length, and ChatMiLu composes it for you."
      testid="music-tool-page"
    >
      <div className="rounded-3xl border border-white/10 bg-panel p-7">
        <label className="text-xs uppercase tracking-widest text-ink-soft">Prompt</label>
        <Textarea
          data-testid="music-prompt-input"
          dir="auto"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A warm lo-fi beat with rain, soft piano and a slow vinyl crackle…"
          className="mt-2 min-h-[110px] rounded-2xl"
        />

        <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Genre</label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="music-genres">
          {(meta?.music_genres || ["None"]).map((g) => (
            <button
              key={g}
              data-testid={`music-genre-${g}`}
              onClick={() => setGenre(g)}
              className={`min-h-[36px] rounded-full px-4 text-sm transition-colors ${
                genre === g ? "bg-brand text-white" : "bg-surfacealt text-ink-soft hover:text-ink"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Duration</label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="music-durations">
          {(meta?.music_durations || [8, 15, 30]).map((d) => (
            <button
              key={d}
              data-testid={`music-duration-${d}`}
              onClick={() => setDuration(d)}
              className={`min-h-[36px] rounded-full px-5 text-sm transition-colors ${
                duration === d ? "bg-forest text-page" : "bg-surfacealt text-ink-soft hover:text-ink"
              }`}
            >
              {d}s
            </button>
          ))}
        </div>

        <Button
          data-testid="music-generate-button"
          onClick={generate}
          disabled={busy}
          className="mt-8 min-h-[48px] rounded-full bg-brand px-8 hover:bg-brand-hover active:scale-95"
        >
          {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Sparkles className="me-2 h-4 w-4" />}
          Generate
        </Button>

        {notice && (
          <p data-testid="music-notice" className="mt-5 rounded-2xl bg-brand/10 px-4 py-3 text-sm text-ink-soft">
            {notice}
          </p>
        )}
      </div>
    </Shell>
  );
}

export function ImageGenerator() {
  const [meta, setMeta] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [negative, setNegative] = useState("");
  const [style, setStyle] = useState("photo");
  const [busy, setBusy] = useState(false);
  const [image, setImage] = useState(null);

  useEffect(() => {
    api.get("/tools/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Describe the image you want");
    setBusy(true);
    try {
      const { data } = await api.post("/tools/image", {
        prompt, style, negative_prompt: negative,
      });
      setImage(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      icon={ImageIcon}
      title="AI Image Generator"
      blurb={`Turn any description into artwork. Pick a style, add what to avoid, and generate.${
        meta?.image_model ? ` Powered by ${meta.image_model}.` : ""
      }`}
      testid="image-tool-page"
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-white/10 bg-panel p-7">
          <label className="text-xs uppercase tracking-widest text-ink-soft">Prompt</label>
          <Textarea
            data-testid="image-prompt-input"
            dir="auto"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A lantern-lit street in Hanoi at dusk, rain reflections, a girl with a red umbrella…"
            className="mt-2 min-h-[110px] rounded-2xl"
          />

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">Style</label>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="image-styles">
            {(meta?.image_styles || []).map((s) => (
              <button
                key={s.id}
                data-testid={`image-style-${s.id}`}
                onClick={() => setStyle(s.id)}
                className={`min-h-[44px] rounded-2xl border px-3 text-sm transition-colors ${
                  style === s.id ? "border-brand bg-brand/15 text-ink" : "border-white/10 bg-page text-ink-soft"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <label className="mt-6 block text-xs uppercase tracking-widest text-ink-soft">
            Negative prompt (what to avoid)
          </label>
          <Input
            data-testid="image-negative-input"
            value={negative}
            onChange={(e) => setNegative(e.target.value)}
            placeholder="blurry, extra fingers, text, watermark"
            className="mt-2 h-11 rounded-xl"
          />

          <Button
            data-testid="image-generate-button"
            onClick={generate}
            disabled={busy}
            className="mt-8 min-h-[48px] w-full rounded-full bg-brand hover:bg-brand-hover active:scale-95"
          >
            {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Sparkles className="me-2 h-4 w-4" />}
            Generate image
          </Button>
        </div>

        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-3xl border border-white/10 bg-panel p-7">
          {busy && <Loader2 className="h-8 w-8 animate-spin text-brand" />}
          {!busy && !image && (
            <p className="max-w-xs text-center text-sm text-ink-soft">
              Your artwork appears here. Try “a red panda barista in a neon Tokyo café”.
            </p>
          )}
          {image && (
            <>
              <img
                data-testid="generated-image"
                src={`data:image/png;base64,${image.image_base64}`}
                alt={image.prompt}
                className="w-full rounded-2xl border border-white/10"
              />
              <a
                data-testid="download-image"
                href={`data:image/png;base64,${image.image_base64}`}
                download="chatmilu-image.png"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-5 py-2.5 text-sm hover:bg-white/20"
              >
                <Download className="h-4 w-4" /> Download
              </a>
            </>
          )}
        </div>
      </div>
    </Shell>
  );
}

const TABS = [
  { id: "vision", label: "AI Vision", icon: Eye },
  { id: "pdf", label: "PDF Summary", icon: FileText },
  { id: "url", label: "Web Page Summary", icon: Link2 },
];

export function Summarizers() {
  const [tab, setTab] = useState("vision");
  const [url, setUrl] = useState("");
  const [question, setQuestion] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState("");
  const imageRef = useRef(null);
  const pdfRef = useRef(null);

  const run = async () => {
    setBusy(true);
    setSummary("");
    try {
      if (tab === "url") {
        const { data } = await api.post("/tools/summarize", { kind: "url", url });
        setSummary(data.summary);
      } else if (tab === "vision") {
        const file = imageRef.current?.files?.[0];
        if (!file) throw { response: { data: { detail: "Choose an image first" } } };
        const fd = new FormData();
        fd.append("image", file);
        fd.append("question", question);
        const { data } = await api.post("/tools/vision", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSummary(data.summary);
      } else {
        const file = pdfRef.current?.files?.[0];
        if (file) {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/tools/pdf", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setSummary(data.summary);
        } else {
          const { data } = await api.post("/tools/summarize", { kind: "text", text });
          setSummary(data.summary);
        }
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      icon={FileText}
      title="AI Summarizers"
      blurb="Understand an image, a PDF or any web page in seconds — summarised in your own language."
      testid="summarizer-tool-page"
    >
      <div className="flex flex-wrap gap-2" data-testid="summarizer-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`summarizer-tab-${t.id}`}
              onClick={() => {
                setTab(t.id);
                setSummary("");
              }}
              className={`inline-flex min-h-[44px] items-center gap-2 rounded-full px-5 text-sm transition-colors ${
                tab === t.id ? "bg-brand text-white" : "bg-surfacealt text-ink-soft hover:text-ink"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6 rounded-3xl border border-white/10 bg-panel p-7">
        {tab === "vision" && (
          <>
            <label className="text-xs uppercase tracking-widest text-ink-soft">Image (JPEG, PNG, WEBP)</label>
            <input
              data-testid="vision-file-input"
              ref={imageRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="mt-2 block w-full rounded-xl border border-white/10 bg-page p-3 text-sm"
            />
            <label className="mt-5 block text-xs uppercase tracking-widest text-ink-soft">
              Question (optional)
            </label>
            <Input
              data-testid="vision-question-input"
              dir="auto"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What does this sign say? Is this receipt correct?"
              className="mt-2 h-11 rounded-xl"
            />
          </>
        )}

        {tab === "pdf" && (
          <>
            <label className="text-xs uppercase tracking-widest text-ink-soft">PDF file</label>
            <input
              data-testid="pdf-file-input"
              ref={pdfRef}
              type="file"
              accept="application/pdf"
              className="mt-2 block w-full rounded-xl border border-white/10 bg-page p-3 text-sm"
            />
            <label className="mt-5 block text-xs uppercase tracking-widest text-ink-soft">
              …or paste text instead
            </label>
            <Textarea
              data-testid="pdf-text-input"
              dir="auto"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste a long report, article or transcript…"
              className="mt-2 min-h-[120px] rounded-2xl"
            />
          </>
        )}

        {tab === "url" && (
          <>
            <label className="text-xs uppercase tracking-widest text-ink-soft">Web page link</label>
            <Input
              data-testid="url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/article"
              className="mt-2 h-11 rounded-xl"
            />
          </>
        )}

        <Button
          data-testid="summarize-button"
          onClick={run}
          disabled={busy}
          className="mt-7 min-h-[48px] rounded-full bg-brand px-8 hover:bg-brand-hover active:scale-95"
        >
          {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <Upload className="me-2 h-4 w-4" />}
          Summarise
        </Button>

        {summary && (
          <div
            data-testid="summary-output"
            dir="auto"
            className="mt-7 whitespace-pre-wrap rounded-2xl bg-page p-5 text-sm leading-relaxed"
          >
            {summary}
          </div>
        )}
      </div>
    </Shell>
  );
}
